import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { EnumType } from 'utils/types';

export interface LocalMedicalReport {
  id: string;
  room_id: number;
  subjective: string;
  objective: string;
  diagnostic_id: string;
  plan?: string;
  consultation_id?: string;
  consultation_type?: string;
}

/**
 * @description The status of the automatic report
 */
export const MedicalReportStatusEnum = {
  Idle: 'idle',
  Active: 'active',
  Loading: 'loading',
  Finished: 'finished',
  Error: 'error',
} as const;

export type MedicalReportStatus = EnumType<typeof MedicalReportStatusEnum>;

/**
 * @description Actions to trigger changes in the automatic report state
 */
export const MedicalReportActionEnum = {
  Start: 'start',
  Loading: 'loading',
  Finished: 'finished',
  Error: 'error',
  Reset: 'reset',
} as const;

export type MedicalReportAction = EnumType<typeof MedicalReportActionEnum>;

interface MedicalReportsState {
  medicalReports: LocalMedicalReport[];
  isAutomatic: boolean;
  automaticReportStatus?: MedicalReportStatus;
  isRecordable: boolean;
}

const initialState: MedicalReportsState = {
  isAutomatic: false,
  medicalReports: [],
  automaticReportStatus: 'idle',
  isRecordable: false,
};

const slice = createSlice({
  name: 'medicalReports',
  initialState,
  reducers: {
    fetchMedicalReports: (state: MedicalReportsState, action) => {
      state.medicalReports = action.payload;
    },
    updateAutomaticReport: (
      state: MedicalReportsState,
      action: PayloadAction<MedicalReportAction>
    ) => {
      switch (action.payload) {
        case MedicalReportActionEnum.Start:
          state.automaticReportStatus = 'active';
          state.isAutomatic = true;
          break;
        case MedicalReportActionEnum.Loading:
          state.automaticReportStatus = 'loading';
          state.isAutomatic = true;
          break;
        case MedicalReportActionEnum.Finished:
          state.automaticReportStatus = 'finished';
          state.isAutomatic = true;
          break;
        case MedicalReportActionEnum.Error:
          state.automaticReportStatus = 'error';
          break;
        case MedicalReportActionEnum.Reset:
        default:
          state.automaticReportStatus = 'idle';
          state.isAutomatic = false;

          break;
      }
    },
    resetAutomaticReport: (state: MedicalReportsState) => {
      state.isAutomatic = false;
      state.automaticReportStatus = 'idle';
    },
    addMedicalReport: (state: MedicalReportsState, action) => {
      const index = state.medicalReports.findIndex(
        (item: LocalMedicalReport) => item.room_id === action.payload.room_id
      );
      if (index !== -1) {
        state.medicalReports[index] = action.payload;
      } else {
        state.medicalReports.push(action.payload);
      }
    },
    updateMedicalReport: (state: MedicalReportsState, action) => {
      const index = state.medicalReports.findIndex(
        (item: LocalMedicalReport) => item.id === action.payload.id
      );
      state.medicalReports[index] = action.payload;
    },
    deleteMedicalReport: (state: MedicalReportsState, action) => {
      state.medicalReports = state.medicalReports.filter(
        (item: LocalMedicalReport) => item.id !== action.payload.id
      );
    },
    updateIsRecordable: (state: MedicalReportsState, action) => {
      state.isRecordable = action.payload.isRecordable;
    },
  },
});

export const {
  addMedicalReport,
  deleteMedicalReport,
  fetchMedicalReports,
  updateMedicalReport,
  updateAutomaticReport,
  resetAutomaticReport,
  updateIsRecordable,
} = slice.actions;
export default slice.reducer;
